import { createReducer, on } from '@ngrx/store';
import {
  clearNonBpsRegistrationState,
  NonBpsAttachmentTypesActions,
  NonBpsLoadOperatorByUuidActions,
  NonBpsProductCategoriesActions,
  NonBpsProgressActions,
  NonBpsRegistrationState,
  NonBpsSectionAActions,
  NonBpsSectionBActions,
  NonBpsSubmitRegistrationActions,
  NonBpsTinVerificationActions,
  NonBpsUploadAttachmentActions,
  NonBpsDeleteAttachmentActions,
  NonBpsViewAttachmentActions,
  setNonBpsOperatorUuid,
  setNonBpsSectionCComplete,
  RequestState,
} from './non-bps-registration.action';
import { handleFailure, handleRequest, handleSuccess } from '@shared/ngrx-factory/factory.reducer';

const initialRequestState: RequestState<any> = {
  loading: false,
  message: null,
  response: null,
  error: null,
};

export const initialNonBpsRegistrationState: NonBpsRegistrationState = {
  progress: { ...initialRequestState },
  attachmentTypes: { ...initialRequestState },
  productCategories: { ...initialRequestState },
  saveSectionA: { ...initialRequestState },
  verifyTin: { ...initialRequestState },
  saveSectionB: { ...initialRequestState },
  uploadAttachment: { ...initialRequestState },
  submitRegistration: { ...initialRequestState },
  loadOperatorByUuid: { ...initialRequestState },
  viewAttachment: { ...initialRequestState },
  deleteAttachment: { ...initialRequestState },
  completionPercentage: 0,
  operatorUuid: null,
  sectionAComplete: false,
  sectionBComplete: false,
  sectionCComplete: false,
  allSectionsComplete: false,
  attachmentTypeList: [],
  productCategoryList: [],
};

export const nonBpsRegistrationReducer = createReducer(
  initialNonBpsRegistrationState,

  on(NonBpsProgressActions.request, (state) => handleRequest(state, 'progress')),
  on(NonBpsProgressActions.success, (state, { response }) => {
    const progress = response?.data?.data;
    return {
      ...handleSuccess(state, 'progress', response),
      completionPercentage: Number(
        progress?.completionPercentage ?? state.completionPercentage ?? 0,
      ),
      operatorUuid: progress?.operatorUuid ?? state.operatorUuid,
      sectionAComplete: Boolean(progress?.sectionAComplete),
      sectionBComplete: Boolean(progress?.sectionBComplete),
      sectionCComplete: Boolean(progress?.sectionCComplete),
      allSectionsComplete: Boolean(progress?.sectionAComplete && progress?.sectionBComplete && progress?.sectionCComplete),
    };
  }),
  on(NonBpsProgressActions.failure, (state, { error }) => handleFailure(state, 'progress', error)),

  on(NonBpsAttachmentTypesActions.request, (state) => handleRequest(state, 'attachmentTypes')),
  on(NonBpsAttachmentTypesActions.success, (state, { response }) => ({
    ...handleSuccess(state, 'attachmentTypes', response),
    attachmentTypeList: response?.data ?? [],
  })),
  on(NonBpsAttachmentTypesActions.failure, (state, { error }) =>
    handleFailure(state, 'attachmentTypes', error),
  ),

  on(NonBpsProductCategoriesActions.request, (state) => handleRequest(state, 'productCategories')),
  on(NonBpsProductCategoriesActions.success, (state, { response }) => ({
    ...handleSuccess(state, 'productCategories', response),
    productCategoryList: response?.data ?? [],
  })),
  on(NonBpsProductCategoriesActions.failure, (state, { error }) =>
    handleFailure(state, 'productCategories', error),
  ),

  on(NonBpsSectionAActions.request, (state) => handleRequest(state, 'saveSectionA')),
  on(NonBpsSectionAActions.success, (state, { response }) => ({
    ...handleSuccess(state, 'saveSectionA', response),
    operatorUuid: response?.data?.data?.uuid ?? state.operatorUuid,
    sectionAComplete: true,
    allSectionsComplete: Boolean(true && state.sectionBComplete && state.sectionCComplete),
  })),
  on(NonBpsSectionAActions.failure, (state, { error }) =>
    handleFailure(state, 'saveSectionA', error),
  ),

  on(NonBpsTinVerificationActions.request, (state) => handleRequest(state, 'verifyTin')),
  on(NonBpsTinVerificationActions.success, (state, { response }) =>
    handleSuccess(state, 'verifyTin', response),
  ),
  on(NonBpsTinVerificationActions.failure, (state, { error }) =>
    handleFailure(state, 'verifyTin', error),
  ),

  on(NonBpsSectionBActions.request, (state) => handleRequest(state, 'saveSectionB')),
  on(NonBpsSectionBActions.success, (state, { response }) => ({
    ...handleSuccess(state, 'saveSectionB', response),
    sectionBComplete: true,
    allSectionsComplete: Boolean(state.sectionAComplete && true && state.sectionCComplete),
  })),
  on(NonBpsSectionBActions.failure, (state, { error }) =>
    handleFailure(state, 'saveSectionB', error),
  ),

  on(NonBpsUploadAttachmentActions.request, (state) => handleRequest(state, 'uploadAttachment')),
  on(NonBpsUploadAttachmentActions.success, (state, { response }) =>
    handleSuccess(state, 'uploadAttachment', response),
  ),
  on(NonBpsUploadAttachmentActions.failure, (state, { error }) =>
    handleFailure(state, 'uploadAttachment', error),
  ),

  on(NonBpsSubmitRegistrationActions.request, (state) =>
    handleRequest(state, 'submitRegistration'),
  ),
  on(NonBpsSubmitRegistrationActions.success, (state, { response }) =>
    handleSuccess(state, 'submitRegistration', response),
  ),
  on(NonBpsSubmitRegistrationActions.failure, (state, { error }) =>
    handleFailure(state, 'submitRegistration', error),
  ),

  on(NonBpsLoadOperatorByUuidActions.request, (state) =>
    handleRequest(state, 'loadOperatorByUuid'),
  ),
  on(NonBpsLoadOperatorByUuidActions.success, (state, { response }) =>
    handleSuccess(state, 'loadOperatorByUuid', response),
  ),
  on(NonBpsLoadOperatorByUuidActions.failure, (state, { error }) =>
    handleFailure(state, 'loadOperatorByUuid', error),
  ),

  on(setNonBpsOperatorUuid, (state, { uuid }) => ({ ...state, operatorUuid: uuid })),
  on(setNonBpsSectionCComplete, (state, { completed }) => ({
    ...state,
    sectionCComplete: completed,
    allSectionsComplete: Boolean(state.sectionAComplete && state.sectionBComplete && completed),
  })),
  on(NonBpsViewAttachmentActions.request, (state) => handleRequest(state, 'viewAttachment')),
  on(NonBpsViewAttachmentActions.success, (state, { response }) =>
    handleSuccess(state, 'viewAttachment', response),
  ),
  on(NonBpsViewAttachmentActions.failure, (state, { error }) =>
    handleFailure(state, 'viewAttachment', error),
  ),
  on(NonBpsViewAttachmentActions.clearState, (state) => ({
    ...state,
    viewAttachment: { ...initialRequestState },
  })),

  on(NonBpsDeleteAttachmentActions.request, (state) => handleRequest(state, 'deleteAttachment')),
  on(NonBpsDeleteAttachmentActions.success, (state, { response }) =>
    handleSuccess(state, 'deleteAttachment', response),
  ),
  on(NonBpsDeleteAttachmentActions.failure, (state, { error }) =>
    handleFailure(state, 'deleteAttachment', error),
  ),
  on(NonBpsDeleteAttachmentActions.clearState, (state) => ({
    ...state,
    deleteAttachment: { ...initialRequestState },
  })),
  on(clearNonBpsRegistrationState, () => ({ ...initialNonBpsRegistrationState })),
);
